import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { useNotifications } from '../../ui/notifications';
import { SiteConfig } from "../../api/Objetos";
import { API_BASE_URL } from '../../api/config';

const defaultConfig: SiteConfig = {
  logoNavbar: "https://cdn.digitalvalue.es/almassora/assets2/697b2b565350fb217dae89b7",
  tituloNavbar: "SUBVENCIONES EUROPEAS AYUNTAMIENTO DE ALMASSORA",
  subtituloNavbar: "Panel de gestión Privada",
  logoFooter: "https://cdn.digitalvalue.es/almassora/assets2/697b2b565350fb217dae89b7",
  direccion: "Plaza de Pere Cornell, 1, 12550, Castellón",
  telefono: "964 56 00 01",
  correoElectronico: "e-ajuntament@almassora.es",
  enlaces: [
    { nombre: "Contacto", url: "https://www.almassora.es/ca/articulos/contacto" },
    { nombre: "Mapa del sitio", url: "https://www.almassora.es/ca/sitemap" },
    { nombre: "Aviso legal", url: "https://www.almassora.es/ca/articulos/aviso-legal" },
    { nombre: "Política de privacidad", url: "https://www.almassora.es/ca/articulos/politica-de-privacidad" },
    { nombre: "Accesibilidad", url: "https://www.almassora.es/ca/articulos/declaracio-d-accessibilitat" },
    { nombre: "Política de cookies", url: "https://www.almassora.es/ca/articulos/politica-de-cookies" }
  ]
};

interface SiteConfigContextType {
  config: SiteConfig;
  setConfig: (newConfig: SiteConfig) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfigState] = useState<SiteConfig>(defaultConfig);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { token, isAuthenticated } = useAuth();
  const { showSuccess, showError } = useNotifications();

  const API_URL = `${API_BASE_URL}/api/configuracion`;

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_URL);
        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`);
        }
        const data: SiteConfig = await response.json();
        setConfigState(data);
      } catch (err: any) {
        console.error("Error al cargar la configuración del sitio:", err);
        setError("No se pudo cargar la configuración del sitio. Usando valores por defecto.");
        setConfigState(defaultConfig);
        showError("Error al cargar la configuración", "No se pudo cargar la configuración del sitio desde el backend. Se están usando valores por defecto.");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [API_URL, showError]);

  const setConfig = async (newConfig: SiteConfig) => {
    if (!isAuthenticated || !token) {
      setError("No estás autenticado para guardar la configuración.");
      showError("Acceso denegado", "No estás autenticado para guardar la configuración. Por favor, inicia sesión.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(API_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newConfig),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error HTTP al guardar: ${response.status} - ${errorText}`);
      }

      const updatedData: SiteConfig = await response.json();
      setConfigState(updatedData);
      showSuccess("Configuración guardada", "La configuración del sitio se ha actualizado correctamente en el backend.");
    } catch (err: any) {
      console.error("Error al guardar la configuración del sitio:", err);
      setError(`Error al guardar la configuración: ${err.message}`);
      showError("Error al guardar la configuración", `No se pudo guardar la configuración del sitio: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteConfigContext.Provider value={{ config, setConfig, loading, error }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
}

export {};
